/**
 * Flow360 Account Browser — browse Flow360 simulation hierarchy and import into Thread.
 *
 * Layout:
 * - Left sidebar: searchable, infinitely-scrolling project list with rich cards
 * - Main area: expandable artifact tree (geometry → surface mesh → volume mesh → case)
 * - Bottom bar: import action
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Heading,
  ScrollArea,
  Text,
  TextField,
} from "@radix-ui/themes";

import {
  fetchFlow360Folders,
  fetchFlow360Geometries,
  fetchFlow360SurfaceMeshes,
  fetchFlow360VolumeMeshes,
  fetchFlow360Cases,
  fetchFlow360CaseResults,
  fetchProjectThumbnail,
  importFlow360Data,
  getStatusColor,
} from "../flow360/api";
import type { FolderPage } from "../flow360/api";
import type {
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
  Flow360Case,
  CaseResults,
} from "../flow360/types";
import { ThreadLogo } from "../components/ThreadLogo";

const PAGE_SIZE = 100;

// ─── SVG icons ──────────────────────────────────────────────────────────────

function GeometryIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M8 1L14.5 5v6L8 15 1.5 11V5L8 1z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="none"
      />
      <path d="M8 1v14M1.5 5L14.5 11M14.5 5L1.5 11" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function SurfaceMeshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1 5.5h14M1 10.5h14M5.5 1v14M10.5 1v14" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
    </svg>
  );
}

function VolumeMeshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M3 12L8 15l5-3V5L8 2 3 5v7z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3 5l5 3 5-3M8 8v7" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
    </svg>
  );
}

function CaseIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 3V2a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2 7h12" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

// ─── Relative time ──────────────────────────────────────────────────────────

function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

// ─── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const color = getStatusColor(status);
  const radixColor =
    color === "success"
      ? "green"
      : color === "warning"
        ? "orange"
        : color === "error"
          ? "red"
          : "gray";
  return (
    <Badge color={radixColor} variant="soft" size="1">
      {status}
    </Badge>
  );
}

function StatusDot({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const color = getStatusColor(status);
  const hex =
    color === "success"
      ? "var(--green-9)"
      : color === "warning"
        ? "var(--orange-9)"
        : color === "error"
          ? "var(--red-9)"
          : "var(--gray-8)";
  return (
    <Box
      style={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: hex,
        flexShrink: 0,
      }}
    />
  );
}

// ─── Project thumbnail ──────────────────────────────────────────────────────

function ProjectThumbnail({ folder }: { folder: Flow360Folder }) {
  const { data } = useQuery({
    queryKey: ["flow360", "thumbnail", folder.id],
    queryFn: () => fetchProjectThumbnail(folder.id),
    enabled: !!folder.thumbnailPath,
    staleTime: 30 * 60_000,
    retry: false,
  });

  if (data?.url) {
    return (
      <Box
        style={{
          width: 48,
          height: 36,
          borderRadius: 4,
          overflow: "hidden",
          background: "var(--gray-a3)",
          flexShrink: 0,
        }}
      >
        <img
          src={data.url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          loading="lazy"
        />
      </Box>
    );
  }

  const icon = folder.rootItemType;
  return (
    <Flex
      align="center"
      justify="center"
      style={{
        width: 48,
        height: 36,
        borderRadius: 4,
        background: "var(--gray-a3)",
        color: "var(--gray-9)",
        flexShrink: 0,
      }}
    >
      {icon === "Geometry" && <GeometryIcon size={18} />}
      {icon === "SurfaceMesh" && <SurfaceMeshIcon size={18} />}
      {icon === "VolumeMesh" && <VolumeMeshIcon size={18} />}
      {!icon && <GeometryIcon size={18} />}
    </Flex>
  );
}

// ─── Project card (sidebar row) ─────────────────────────────────────────────

function ProjectCard({
  folder,
  selected,
  onClick,
}: {
  folder: Flow360Folder;
  selected: boolean;
  onClick: () => void;
}) {
  const stats = folder.statistics;
  const caseCount = stats?.Case?.count ?? 0;
  const geoCount = stats?.Geometry?.count ?? 0;

  return (
    <Box
      px="3"
      py="2"
      style={{
        cursor: "pointer",
        background: selected ? "var(--accent-a3)" : "transparent",
        borderLeft: selected
          ? "3px solid var(--accent-9)"
          : "3px solid transparent",
      }}
      onClick={onClick}
    >
      <Flex gap="2" align="start">
        <ProjectThumbnail folder={folder} />
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Flex align="center" gap="1" mb="1">
            <StatusDot status={folder.rootItemDisplayStatus ?? folder.rootItemStatus} />
            <Text
              size="2"
              weight={selected ? "bold" : "medium"}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {folder.name}
            </Text>
          </Flex>
          <Flex gap="2" align="center" wrap="wrap">
            {geoCount > 0 && (
              <Text size="1" color="gray">
                {geoCount} geo
              </Text>
            )}
            {caseCount > 0 && (
              <Text size="1" color="gray">
                {caseCount} case{caseCount !== 1 ? "s" : ""}
              </Text>
            )}
            {folder.solverVersion && (
              <Text size="1" color="gray">
                {folder.solverVersion}
              </Text>
            )}
            {folder.createdAt && (
              <Text size="1" color="gray">
                {relativeTime(folder.createdAt)}
              </Text>
            )}
          </Flex>
          {folder.tags && folder.tags.length > 0 && (
            <Flex gap="1" mt="1" wrap="wrap">
              {folder.tags.slice(0, 3).map((t) => (
                <Badge key={t} variant="outline" size="1" color="gray">
                  {t}
                </Badge>
              ))}
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
}

// ─── Inline result chips ────────────────────────────────────────────────────

function ResultChips({ results }: { results: CaseResults | undefined }) {
  if (!results?.outputs) return null;
  const keys = ["CL", "CD", "CMy", "CMpitching"];
  const shown = keys.filter((k) => results.outputs![k] != null);
  if (shown.length === 0) return null;
  return (
    <Flex gap="2" wrap="wrap">
      {shown.map((k) => (
        <Badge key={k} variant="outline" size="1">
          {k}={Number(results.outputs![k]).toPrecision(4)}
        </Badge>
      ))}
      {results.inputs?.alpha != null && (
        <Badge variant="outline" color="blue" size="1">
          α={results.inputs.alpha}°
        </Badge>
      )}
      {results.inputs?.mach != null && (
        <Badge variant="outline" color="blue" size="1">
          M={results.inputs.mach}
        </Badge>
      )}
    </Flex>
  );
}

// ─── Expandable case row ────────────────────────────────────────────────────

function CaseRow({ c }: { c: Flow360Case }) {
  const [expanded, setExpanded] = useState(false);

  const { data: results } = useQuery({
    queryKey: ["flow360", "case-results", c.id],
    queryFn: () => fetchFlow360CaseResults(c.id),
    enabled: c.status === "completed",
    staleTime: 5 * 60_000,
  });

  return (
    <Box ml="7" mb="1">
      <Flex
        align="center"
        gap="2"
        py="1"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Text size="2" style={{ fontFamily: "monospace" }}>
          {expanded ? "▾" : "▸"}
        </Text>
        <CaseIcon />
        <Text size="2" weight="medium">
          {c.name}
        </Text>
        <StatusBadge status={c.status} />
        <ResultChips results={results} />
      </Flex>

      {expanded && results && (
        <Box ml="4" mb="2" p="2" style={{ background: "var(--gray-a3)", borderRadius: 6 }}>
          {results.inputs && (
            <Box mb="2">
              <Text size="1" color="gray" weight="bold">
                Inputs
              </Text>
              <Flex gap="3" mt="1" wrap="wrap">
                {Object.entries(results.inputs).map(([k, v]) => (
                  <Text key={k} size="1">
                    {k}: {typeof v === "number" ? v.toPrecision(5) : String(v)}
                  </Text>
                ))}
              </Flex>
            </Box>
          )}
          {results.outputs && (
            <Box>
              <Text size="1" color="gray" weight="bold">
                Force Coefficients
              </Text>
              <Box
                mt="1"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 4,
                }}
              >
                {Object.entries(results.outputs).map(([k, v]) => (
                  <Text key={k} size="1" style={{ fontFamily: "monospace" }}>
                    {k}: {typeof v === "number" ? v.toPrecision(6) : String(v)}
                  </Text>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Volume mesh group ──────────────────────────────────────────────────────

function VolumeMeshGroup({ vm }: { vm: Flow360VolumeMesh }) {
  const [open, setOpen] = useState(false);

  const { data: cases } = useQuery({
    queryKey: ["flow360", "cases", vm.id],
    queryFn: () => fetchFlow360Cases({ volumeMeshId: vm.id }),
    enabled: open,
  });

  return (
    <Box ml="5" mb="1">
      <Flex
        align="center"
        gap="2"
        py="1"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <Text size="2" style={{ fontFamily: "monospace" }}>
          {open ? "▾" : "▸"}
        </Text>
        <VolumeMeshIcon />
        <Text size="2">{vm.name}</Text>
        <StatusBadge status={vm.status} />
      </Flex>
      {open && cases?.map((c) => <CaseRow key={c.id} c={c} />)}
    </Box>
  );
}

// ─── Surface mesh group ─────────────────────────────────────────────────────

function SurfaceMeshGroup({ sm }: { sm: Flow360SurfaceMesh }) {
  const [open, setOpen] = useState(false);

  const { data: volumeMeshes } = useQuery({
    queryKey: ["flow360", "volume-meshes", sm.id],
    queryFn: () => fetchFlow360VolumeMeshes({ surfaceMeshId: sm.id }),
    enabled: open,
  });

  return (
    <Box ml="3" mb="1">
      <Flex
        align="center"
        gap="2"
        py="1"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <Text size="2" style={{ fontFamily: "monospace" }}>
          {open ? "▾" : "▸"}
        </Text>
        <SurfaceMeshIcon />
        <Text size="2">{sm.name}</Text>
        <StatusBadge status={sm.status} />
      </Flex>
      {open &&
        volumeMeshes?.map((vm) => <VolumeMeshGroup key={vm.id} vm={vm} />)}
    </Box>
  );
}

// ─── Geometry group ─────────────────────────────────────────────────────────

function GeometryGroup({ geom }: { geom: Flow360Geometry }) {
  const [open, setOpen] = useState(false);

  const { data: surfaceMeshes } = useQuery({
    queryKey: ["flow360", "surface-meshes", geom.id],
    queryFn: () => fetchFlow360SurfaceMeshes({ geometryId: geom.id }),
    enabled: open,
  });

  return (
    <Card mb="2" style={{ background: "var(--gray-a2)" }}>
      <Flex
        align="center"
        gap="2"
        py="1"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <Text size="2" style={{ fontFamily: "monospace" }}>
          {open ? "▾" : "▸"}
        </Text>
        <GeometryIcon />
        <Text size="3" weight="medium">
          {geom.name}
        </Text>
        <StatusBadge status={geom.status} />
      </Flex>
      {open &&
        surfaceMeshes?.map((sm) => <SurfaceMeshGroup key={sm.id} sm={sm} />)}
    </Card>
  );
}

// ─── Import dialog ──────────────────────────────────────────────────────────

function ImportDialog({
  folderId,
  folderName,
  open,
  onClose,
}: {
  folderId: string;
  folderName: string;
  open: boolean;
  onClose: () => void;
}) {
  const [newProjectName, setNewProjectName] = useState(folderName);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: importFlow360Data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      // Navigate to the new project with the import job ID for progress tracking
      navigate(`/project/${data.project_id}?importJob=${data.job_id}`);
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Import to Thread</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Import all completed cases from &quot;{folderName}&quot; into a new
          Thread project. Geometries, meshes, and cases will be created as nodes
          with full provenance edges.
        </Dialog.Description>

        <Box mb="4">
          <Text size="2" weight="bold" mb="1">
            New project name
          </Text>
          <TextField.Root
            value={newProjectName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewProjectName(e.target.value)
            }
            placeholder="Project name"
          />
        </Box>

        {mutation.isError && (
          <Box
            mb="3"
            p="2"
            style={{ background: "var(--red-a3)", borderRadius: 6 }}
          >
            <Text size="2" color="red">
              {(mutation.error as Error).message}
            </Text>
          </Box>
        )}

        <Flex gap="3" justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={() =>
              mutation.mutate({
                folder_id: folderId,
                project_name: newProjectName || folderName,
              })
            }
            disabled={mutation.isPending || !newProjectName.trim()}
          >
            {mutation.isPending ? "Starting import..." : "Import"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function Flow360Browser() {
  const [selectedFolder, setSelectedFolder] = useState<Flow360Folder | null>(
    null,
  );
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  const {
    data: foldersData,
    isLoading: foldersLoading,
    isError: foldersError,
    error: foldersErrorObj,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<FolderPage>({
    queryKey: ["flow360", "folders"],
    queryFn: ({ pageParam }) =>
      fetchFlow360Folders({
        page: pageParam as number,
        size: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage * lastPage.size < lastPage.total ? nextPage : undefined;
    },
    retry: false,
  });

  const tokenExpired =
    foldersError &&
    (foldersErrorObj?.message?.includes("401") ||
      foldersErrorObj?.message?.toLowerCase().includes("expired") ||
      foldersErrorObj?.message?.toLowerCase().includes("invalid") ||
      foldersErrorObj?.message?.toLowerCase().includes("reconnect"));

  const allFolders = useMemo(
    () => foldersData?.pages.flatMap((p) => p.projects) ?? [],
    [foldersData],
  );
  const totalProjects = foldersData?.pages[0]?.total ?? 0;

  // When the user types a search, eagerly load all remaining pages so the
  // client-side filter has the full dataset (Flow360 API has no server search).
  useEffect(() => {
    if (search.trim() && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [search, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const folders = useMemo(() => {
    if (!search.trim()) return allFolders;
    const q = search.toLowerCase();
    return allFolders.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.tags?.some((t) => t.toLowerCase().includes(q)),
    );
  }, [allFolders, search]);

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    if (!sentinelRef.current || !hasNextPage || isFetchingNextPage) return;
    const el = sentinelRef.current;
    const parent = el.parentElement;
    if (!parent) return;
    const rect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    if (rect.top < parentRect.bottom + 200) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const viewport = el.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) return;
    const handler = () => handleScroll();
    viewport.addEventListener("scroll", handler, { passive: true });
    return () => viewport.removeEventListener("scroll", handler);
  }, [handleScroll]);

  // Also trigger check on data change
  useEffect(() => {
    handleScroll();
  }, [folders.length, handleScroll]);

  const { data: geometries, isLoading: geoLoading } = useQuery({
    queryKey: ["flow360", "geometries", selectedFolder?.id],
    queryFn: () => fetchFlow360Geometries(selectedFolder!.id),
    enabled: !!selectedFolder,
  });

  return (
    <Box style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <Flex
        align="center"
        justify="between"
        px="3"
        py="2"
        style={{
          borderBottom: "1px solid var(--gray-a4)",
          background: "var(--color-background)",
          flexShrink: 0,
        }}
      >
        <Flex align="center" gap="3">
          <Link to="/" style={{ textDecoration: "none" }}>
            <ThreadLogo height={22} />
          </Link>
          <Box
            style={{ width: 1, height: 20, background: "var(--gray-a5)" }}
          />
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            <Button variant="ghost" size="1" color="gray">
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                <path
                  d="M6.85 3.15a.5.5 0 0 1 0 .7L3.56 7H12.5a.5.5 0 0 1 0 1H3.56l3.29 3.15a.5.5 0 1 1-.7.7l-4-3.85a.5.5 0 0 1 0-.7l4-3.85a.5.5 0 0 1 .7 0z"
                  fill="currentColor"
                />
              </svg>
              Projects
            </Button>
          </Link>
          <Text size="2" color="gray">
            /
          </Text>
          <Text size="2" weight="medium">
            Flow360 Account
          </Text>
          {foldersError ? (
            <Badge color="red" variant="soft" size="1">
              {tokenExpired ? "Not Authenticated" : "Error"}
            </Badge>
          ) : foldersLoading ? (
            <Badge color="gray" variant="soft" size="1">
              Loading...
            </Badge>
          ) : (
            <Badge color="green" variant="soft" size="1">
              {search.trim()
                ? `${folders.length} matching${hasNextPage ? " (loading...)" : ""}`
                : `${allFolders.length} of ${totalProjects} projects`}
            </Badge>
          )}
        </Flex>
      </Flex>

      {/* Body */}
      <Flex style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* Folder sidebar */}
        <Box
          style={{
            width: 320,
            borderRight: "1px solid var(--gray-a5)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box px="3" py="2" style={{ flexShrink: 0 }}>
            <TextField.Root
              placeholder="Search projects..."
              size="2"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
            >
              <TextField.Slot>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M10 6.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0zm-.3 3.9a4.5 4.5 0 1 1 .7-.7l3.1 3.1a.5.5 0 0 1-.7.7L9.7 10.4z"
                    fill="currentColor"
                  />
                </svg>
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <ScrollArea ref={scrollRef} style={{ flex: 1, minHeight: 0 }}>
            {foldersLoading && (
              <Box px="3" py="2">
                <Text size="2" color="gray">
                  Loading projects...
                </Text>
              </Box>
            )}
            {folders.map((f) => (
              <ProjectCard
                key={f.id}
                folder={f}
                selected={selectedFolder?.id === f.id}
                onClick={() => setSelectedFolder(f)}
              />
            ))}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingNextPage && (
              <Box px="3" py="2">
                <Text size="1" color="gray">
                  Loading more...
                </Text>
              </Box>
            )}
            {tokenExpired && (
              <Box px="3" py="2">
                <Text size="2" color="red">
                  Flow360 authentication failed. Check backend API key
                  configuration.
                </Text>
              </Box>
            )}
            {!foldersLoading && !tokenExpired && folders.length === 0 && (
              <Box px="3" py="2">
                <Text size="2" color="gray">
                  No projects found
                </Text>
              </Box>
            )}
          </ScrollArea>
        </Box>

        {/* Main content */}
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          <Box p="4">
            {!selectedFolder && (
              <Text color="gray" size="3">
                Select a project from the sidebar to browse its contents.
              </Text>
            )}

            {selectedFolder && (
              <>
                <Heading size="4" mb="3">
                  {selectedFolder.name}
                </Heading>

                {geoLoading && (
                  <Text color="gray">Loading geometries...</Text>
                )}

                {geometries?.length === 0 && (
                  <Text color="gray">No geometries in this project.</Text>
                )}

                {geometries?.map((geom) => (
                  <GeometryGroup key={geom.id} geom={geom} />
                ))}
              </>
            )}
          </Box>
        </ScrollArea>
      </Flex>

      {/* Bottom bar */}
      {selectedFolder && (
        <Flex
          align="center"
          justify="between"
          px="4"
          py="3"
          style={{ borderTop: "1px solid var(--gray-a5)" }}
        >
          <Text size="2" color="gray">
            {geometries?.length ?? 0} geometries in {selectedFolder.name}
          </Text>
          <Button onClick={() => setImportOpen(true)}>Import to Thread</Button>
        </Flex>
      )}

      {selectedFolder && (
        <ImportDialog
          folderId={selectedFolder.id}
          folderName={selectedFolder.name}
          open={importOpen}
          onClose={() => setImportOpen(false)}
        />
      )}
    </Box>
  );
}
